import { useCallback, useContext, useEffect, useReducer, useRef } from 'react';
import { useLocation, useParams, useRouteMatch } from 'react-router-dom';

import { ConfigContext } from 'Context/config';

import { assignSectionRoutes } from 'Utilities/routing';
import { fetchBlogData, fetchPageData, getGuidePage } from 'Utilities/wordpressPages';

import { WordpressData, WordpressRouteParams } from '../@types/Wordpress';
import { TIMING } from '../utilities/WordpressConstants';

import { useScroll } from './useScroll';
import { ProcessedBlogData, ProcessedPageData, useWordpressDataFormatHelpers } from './useWordpressDataFormatHelpers';

type WordpressState = {
  data: WordpressData | null;
  empty: boolean;
  error: boolean;
  isBlog: boolean;
  isGuide?: boolean;
  isPage: boolean;
  isSSR: boolean;
  ready: boolean;
};

type WordpressAction = {
  payload?: { data: WordpressData; empty?: boolean; isBlog?: boolean; isPage?: boolean };
  type: 'ERROR' | 'SUCCESS' | 'LOADING';
};

export const useWPData = () => {
  const location = useLocation();
  const params = useParams<WordpressRouteParams>();
  const match = useRouteMatch();

  const context = useContext(ConfigContext);
  const ssrData = (context)?.configState?.wordpressData || null;
  const { processBlogData, processPageData } = useWordpressDataFormatHelpers();
  const { scrollToHash } = useScroll();

  const { isBlogRoute, isGuideRoute } = assignSectionRoutes(match.path);
  const previousPathRef = useRef(location.pathname);
  const isInitialMount = useRef(true);

  // Check if SSR data is valid and non-empty
  const ssrMatchesRoute = ssrData && (
    (ssrData.posts && ssrData.posts.length > 0)
    || (ssrData.content && ssrData.content.length > 0)
  );

  // Format SSR data for initial state if available
  let formattedSSRData: ProcessedBlogData | ProcessedPageData | null = null;
  if (ssrMatchesRoute) {
    const formatData = isBlogRoute ? processBlogData : processPageData;
    formattedSSRData = formatData?.(ssrData);
  }

  const initialState: WordpressState = {
    data: formattedSSRData?.data || null,
    empty: (formattedSSRData as ProcessedBlogData)?.empty || false,
    error: false,
    isBlog: isBlogRoute,
    isGuide: isGuideRoute,
    isPage: !isBlogRoute,
    isSSR: !!ssrMatchesRoute,
    ready: !!formattedSSRData,
  };

  const [state, dispatch] = useReducer((currentState: WordpressState, action: WordpressAction) => {
    switch (action.type) {
    case 'LOADING':
      return { ...currentState, error: false, ready: false };
    case 'SUCCESS':
      return { ...currentState, ...action.payload, error: false, ready: true };
    case 'ERROR':
      return { ...currentState, data: null, empty: true, error: true, ready: true };
    default:
      return currentState;
    }
  }, initialState);

  const fetchBlogDataHandler = useCallback(() => {
    fetchBlogData(location.pathname)
      .then((data) => {
        if (data) {
          const formatted = processBlogData(data);
          if (formatted) {
            dispatch({ payload: { ...formatted, isPage: undefined }, type: 'SUCCESS' });
          } else {
            throw new Error('Blog data formatting failed');
          }
        } else {
          throw new Error('No blog data received');
        }
      })
      .catch((error) => {
        globalThis?.Sentry?.captureException(error as Error, {
          extra: {
            path: location.pathname,
            type: 'blog',
          },
        });
        dispatch({ type: 'ERROR' });
      });
  }, [location.pathname, processBlogData]);

  const fetchPageDataHandler = useCallback(() => {
    const urlSlug = params?.subdir || location.pathname.substring(1);
    const fetchPromise = isGuideRoute
      ? getGuidePage({ route: params?.subdir, slug: location.pathname })
      : fetchPageData(urlSlug, isGuideRoute);

    fetchPromise
      .then((data) => {
        const formatted = processPageData(data);
        if (formatted) {
          dispatch({ payload: { ...formatted, isBlog: undefined }, type: 'SUCCESS' });
        } else {
          throw new Error('Page data formatting failed');
        }
      })
      .catch((error) => {
        globalThis?.Sentry?.captureException(error as Error, {
          extra: {
            path: location.pathname,
            type: isGuideRoute ? 'guide' : 'page',
          },
        });
        dispatch({ type: 'ERROR' });
      });
  }, [params?.subdir, location.pathname, isGuideRoute, processPageData]);

  useEffect(() => {
    const isPathChanged = previousPathRef.current !== location.pathname;

    if ((isPathChanged || isInitialMount.current) && (isPathChanged || !formattedSSRData)) {
      dispatch({ type: 'LOADING' });

      if (isBlogRoute) {
        fetchBlogDataHandler();
      } else {
        fetchPageDataHandler();
      }

      previousPathRef.current = location.pathname;
      isInitialMount.current = false;
    }
  }, [location.pathname, isBlogRoute, fetchBlogDataHandler, fetchPageDataHandler, formattedSSRData]);

  useEffect(() => {
    if (location.hash && state.ready) {
      setTimeout(() => scrollToHash(), TIMING.SCROLL_DELAY);
    }
  }, [location.hash, state.ready, scrollToHash]);

  return { ...state };
};
